import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { qk, readOrgScoped, type WorkOrderRow } from "../../hooks/queries";

// Point-file completion is the ingest job, not the upload response. Back off
// the same way a new field's boundary parse is polled, and stop at about two
// minutes so a stuck job does not refresh the list forever.
export const WORK_ORDER_RESULT_POLL_MS = [
  2000, 4000, 8000, 12000, 16000, 24000, 30000, 24000,
];

export function useWorkOrderResultPoll(requestId: string | null) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!requestId) return;
    const delays = WORK_ORDER_RESULT_POLL_MS;
    let i = 0;
    let timer = 0;
    let alive = true;
    const tick = async () => {
      await qc.refetchQueries({ queryKey: qk.workOrders });
      if (!alive) return;
      const rows = readOrgScoped<WorkOrderRow[]>(qc, qk.workOrders) ?? [];
      const row = rows.find((r) => r.request_id === requestId);
      if (row?.status.toLowerCase() === "completed") return;
      if (i < delays.length)
        timer = window.setTimeout(() => void tick(), delays[i++]);
    };
    timer = window.setTimeout(() => void tick(), delays[i++]);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [qc, requestId]);
}
